import { useQueryClient } from '@tanstack/react-query';
import {
  LocationVDriveEntity,
  VDriveCardList,
  queryKeys,
  removeVDriveSecondaryCard,
  swapVDriveCards,
  useBusyAction,
} from '@vgrubs/components';
import { Dispatch, SetStateAction, useState } from 'react';
import { useReceivablesLocation } from '../Receivables/hooks/useReceivablesLocation';
import { EditCard } from './onboarding/EditCard';

type Props = {
  vDrive: LocationVDriveEntity;
  /** Refetches the vDrive, which carries both cards afterwards. */
  onChanged: () => void;
  /** The page spinner, which the card forms drive while they save. */
  setLoading: Dispatch<SetStateAction<boolean>>;
};

/**
 * The location's two vDrive cards (`VDriveCardList`, the same section the
 * merchant sees) on the admin routes, for admin and support: both forms,
 * swap and remove.
 *
 * The vDrive answer carries only the last digits; the brand and whether the
 * default is declining come from the location's receivables detail (the
 * merchant's outstanding balance as admin sees it). Until it loads the rows
 * show a generic card.
 *
 * Renders nothing while the location is on the vGrubs vDrive card or has no
 * own card: the backup needs the owner's own default first.
 */
export const VDriveCards = ({ vDrive, onChanged, setLoading }: Props) => {
  const locationId = vDrive.location.id;
  const queryClient = useQueryClient();
  const { detail } = useReceivablesLocation(locationId);
  const [form, setForm] = useState<'default' | 'backup' | null>(null);
  const { busy, run } = useBusyAction();

  if (vDrive.isVDriveCard || !vDrive.last4) return null;

  const cards = detail?.current.vDrive;
  const backupLast4 = vDrive.secondaryLast4 ?? null;

  // The cards changed: the vDrive for the digits, the detail for the brands.
  const refresh = () => {
    onChanged();
    queryClient.invalidateQueries({
      queryKey: queryKeys.admin.receivables.location(locationId),
    });
  };

  return (
    <>
      <VDriveCardList
        defaultCard={{ brand: cards?.card?.brand ?? null, last4: vDrive.last4 }}
        backupCard={
          backupLast4
            ? { brand: cards?.secondaryCard?.brand ?? null, last4: backupLast4 }
            : null
        }
        defaultDeclined={!!cards?.primaryCardFailed}
        onEditDefault={() => setForm('default')}
        onEditBackup={() => setForm('backup')}
        onSwap={() =>
          run(async () => {
            await swapVDriveCards(locationId);
            refresh();
          }, 'The cards are swapped.')
        }
        onRemove={() =>
          run(async () => {
            await removeVDriveSecondaryCard(locationId);
            refresh();
          }, 'The backup card is removed.')
        }
        busy={busy}
      />

      <EditCard
        secondary={form === 'backup'}
        adding={form === 'backup' && !backupLast4}
        modalIsOpen={form !== null}
        closeModal={() => setForm(null)}
        id={String(locationId)}
        setLoading={setLoading}
        handleGetVDrive={refresh}
      />
    </>
  );
};
